/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, lazy, Suspense } from 'react'
import { useState } from 'react'

// ── Critical (above fold) — eager
import HeroSection from '../components/hero'
import Footer from '../layout/Footer'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import FranceFlag from '../assets/svgs/flag-france.svg'
import GermanyFlag from '../assets/svgs/flag-germany.svg'
import SpainFlag from '../assets/svgs/flag-spain.svg'
import EngFlag from '../assets/svgs/flag-uk.svg'
import { setCookie } from '../utils/cookies'
import { logEvent } from 'firebase/analytics'
import { analytics, firestore } from '../firebase'
import { addDoc, collection } from '@firebase/firestore'
import { Helmet } from 'react-helmet';
import { getJsonLdSchemas, getSeoMetaTags } from '../configs/seoConfig';
import "../components/features/para.css"
import SectionNav from '../components/common/SectionNav'

// ── Below fold — lazy (keeps Swiper, AOS, and heavy UI out of initial parse)
const AosInit = lazy(() => import('aos').then(m => { m.default.init({ once: true, duration: 700 }); return { default: () => null } }))
const FeatureBand = lazy(() => import('../components/hero/FeatureBand'))
const TrustBand = lazy(() => import('../components/hero/TrustBand'))
const NewUseCase = lazy(() => import('../components/useCases/NewUseCase'))
const NewFeatureSection = lazy(() => import('../components/features/NewFeatureSection'))
const WaitListBand = lazy(() => import('../components/common/WaitListBand'))
const Faqs = lazy(() => import('../components/faqs'))
const StoreIcons = lazy(() => import('../components/common/StoreIcons'))
const IconOptions = lazy(() => import('../components/iconOptions'))
const VideoLoader = lazy(() => import('../components/loader/VideoLoader'))
const FeatureShowcase = lazy(() => import('../components/swipers/FeatureShowcase'))
const GettingStarted = lazy(() => import('../components/useCases/GettingStarted'))


export const languagePptions = [
    { value: 'en', label: 'EN', icon: EngFlag },
    { value: 'de', label: 'DE', icon: GermanyFlag },
    { value: 'fr', label: 'FR', icon: FranceFlag },
    { value: 'es', label: 'ES', icon: SpainFlag },
]



function Home({ showSignUpPopup, setShowSignUpPopup }) {
    const { t, i18n } = useTranslation()
    const pageTitle = t('meta.home.pageTitle');
    const pageDescription = t('meta.home.pageDescription');

    const navigate = useNavigate()

    const [isLoading, setIsLoading] = useState(false) // Video intro loader disabled for performance
    // const [isLoading, setIsLoading] = useState(false)
    const [activeLanguage, setActiveLanguage] = useState(languagePptions[0])
    const [openModal, setOpenModal] = useState(false)
    const [userEmail, setUserEmail] = useState('')
    const [isFormLoading, setIsFormLoading] = useState(false)
    const modalOpenFromScroll = useRef(false)
    const [language, setLanguage] = useState('')

    const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

    const featureData = t('homeFeatures', { returnObjects: true });
    const gettingStartedData = t('gettingStarted', { returnObjects: true });



    useEffect(() => {
        if (window.innerWidth <= 768) setIsLoading(false)
    }, [])

    const validateEmail = (email) => {
        return email.match(
            // eslint-disable-next-line no-useless-escape
            /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    }

    const submitEmail = async (type) => {
        try {
            setIsFormLoading(true)
            logEvent(analytics, 'button_click', {
                name: 'submit_button',
                type: type,
            })
            const collectionType =
                type === 'player' ? 'playersCollection' : 'coachesCollection'

            const ref = collection(firestore, collectionType)
            await addDoc(ref, {
                email: userEmail,
            })
            setIsFormLoading(false)
            navigate('/thank-you')
        } catch (error) {
            setIsFormLoading(false)
        }
    }


    useEffect(() => {
        const handleScroll = () => {
            const featureSection = document.getElementById('feature-section')
            if (!isLoading && featureSection) {
                const rect = featureSection.getBoundingClientRect()
                const isVisible =
                    rect.top <= 0 && rect.bottom <= window.innerHeight
                if (isVisible && !openModal && !modalOpenFromScroll.current) {
                    modalOpenFromScroll.current = true
                    setOpenModal(true)
                    window.removeEventListener('scroll', handleScroll)
                }
            }
        }

        if (!modalOpenFromScroll.current)
            window.addEventListener('scroll', handleScroll)

        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [isLoading, openModal])

    const location = useLocation();
    useEffect(() => {
        if (location.hash) {
            const id = location.hash.replace('#', '');
            
            // Try scrolling immediately if the element is already in DOM
            const immediateElement = document.getElementById(id);
            if (immediateElement) {
                setTimeout(() => {
                    immediateElement.scrollIntoView({ behavior: 'smooth' });
                }, 100);
                return;
            }

            // Otherwise, poll for the element (handles lazy-loaded elements under Suspense)
            let attempts = 0;
            const interval = setInterval(() => {
                const element = document.getElementById(id);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                    clearInterval(interval);
                }
                attempts++;
                if (attempts > 30) { // Timeout after 3 seconds
                    clearInterval(interval);
                }
            }, 100);

            return () => clearInterval(interval);
        }
    }, [location.hash]);


    const appName = t('meta.appName');
    const appDescription = t('meta.appDescription');
    const appUrl = "https://inter.tennis";
    const appImage = "https://inter.tennis/Logo_rect_tilted_128.png";
    const fullTitle = `${appName} - ${pageTitle}`;

    return (
        <>
            <Helmet>
                {getSeoMetaTags(pageTitle, pageDescription, "", t('meta.keywords'))}
                {getJsonLdSchemas()}
            </Helmet>

            {isLoading ? (
                <Suspense fallback={null}>
                    <VideoLoader setIsLoading={setIsLoading} />
                </Suspense>
            ) : (
                <>
                    {/* Vertical section nav — desktop only */}
                    <SectionNav onStartClick={() => setShowSignUpPopup(true)} />

                    {/* Semantic SEO content (hidden visually) */}
                    <div style={{ position: 'absolute', width: '1px', height: '1px', padding: '0', margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', border: '0' }}>
                        <h2>3D Tennis Analytics Features</h2>
                        <nav>
                            <ul>
                                <li><a href="/features">Advanced AI Features</a></li>
                                <li><a href="/how-to-use">Setup Guide</a></li>
                                <li><a href="/referral-program">Referral Rewards</a></li>
                            </ul>
                        </nav>
                        <h2>Improve Your Game</h2>
                        <p>Track your tennis strokes, analyze ball speed and placement with our AI-powered video analysis. Available for iOS and Android.</p>
                    </div>

                    <div className="relative" >
                        <div className="flex"  >
                            <div className="flex-col gap-4 w-max min-w-full container mx-auto  ">
                                <HeroSection
                                    setShowSignUpPopup={setShowSignUpPopup}
                                />

                                {/* Below fold — lazy: downloads after hero is painted */}
                                <Suspense fallback={null}>
                                    <AosInit />
                                    <FeatureBand />
                                    <TrustBand />
                                    <StoreIcons />

                                    <FeatureShowcase features={featureData} />

                                    <WaitListBand
                                        buttonName={'navbar.features'}
                                        btnHref="/features"
                                    />

                                    <GettingStarted data={gettingStartedData} />
                                    {/* Desktop WaitListBand — CSS only, no JS media query */}
                                    <div className='hidden md:flex flex-col justify-center mt-28'>
                                        <WaitListBand
                                            buttonName={'navbar.how_to_use'}
                                            btnHref="/how-to-use"
                                        />
                                    </div>

                                    <div className='md:hidden'>
                                        <WaitListBand
                                            buttonName={'navbar.how_to_use'}
                                            btnHref="/how-to-use"
                                        />
                                    </div>

                                    <Faqs />
                                    <Footer />
                                </Suspense>
                            </div>
                        </div>
                    </div>
                    {/* <Modal show={openModal} onClose={() => setOpenModal(false)}>
                        <Modal.Header>{t('modals.try_beta_free')}</Modal.Header>
                    <Modal.Body>
                            <div className="">
                                <label
                                    htmlFor="email"
                                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    Your Email{' '}
                                    <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    id="email"
                                    className={` bg-gray-50 border border-gray-300 shadow-none focus:shadow-none text-gray-900 text-sm rounded-lg block w-full p-2.5 `}
                                    placeholder="name@gmail.com"
                                    required
                                    onChange={(e) => {
                                        const val = e.target.value
                                        setUserEmail(val)
                                    }}
                                    value={userEmail}
                                />
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <div className="flex gap-4 w-full max-md:flex-col">
                                <button
                                    onClick={() => submitEmail('coach')}
                                    className={`
                              ${
                                  validateEmail(userEmail) && !isFormLoading
                                      ? ''
                                      : 'pointer-events-none cursor-not-allowed opacity-50'
                              }
                              flex-1 hover:scale-105 transition-all bg-primaryYellow py-2 px-6 rounded font-semibold tracking-wide text-lg cursor-pointer
                              `}
                                >
                                    {t('hero.rightPane.as_coach')}
                                </button>
                                <button
                                    onClick={() => submitEmail('player')}
                                    className={`
                              ${
                                  validateEmail(userEmail) && !isFormLoading
                                      ? ''
                                      : 'pointer-events-none cursor-not-allowed opacity-50'
                              }
                              flex-1 hover:scale-105 transition-all bg-primaryYellow py-2 px-6 rounded font-semibold tracking-wide text-lg cursor-pointer
                              `}
                                >
                                    {t('hero.rightPane.as_player')}
                                </button>
                            </div>
                        </Modal.Footer>
                    </Modal> */}
                </>
            )}
        </>
    )
}

export default Home
